import React from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Dimensions,
  ScrollView,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import DLOButton from './DLOButton';
import DLODropdown from './DLODropdown';
import { DLODropdownOption } from '../types/dlo.types';
import { useTheme } from '../../../theme/useTheme';

// Close Icon from Figma
const CloseIcon = ({ color = '#3A3A3A', size = 24 }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
      fill={color}
    />
  </Svg>
);

export interface DLOModalProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  children?: React.ReactNode;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onPrimaryPress?: () => void;
  onSecondaryPress?: () => void;
  primaryButtonDisabled?: boolean;
  primaryButtonLoading?: boolean;
  hideButtons?: boolean;
  width?: number | string;
  maxWidth?: number;
  showCloseIcon?: boolean;
  titleAlign?: 'left' | 'center';
  customStyle?: any;
}

const DLOModal: React.FC<DLOModalProps> = ({
  visible,
  onClose,
  title = 'Modal Title',
  children,
  primaryButtonText = 'Save',
  secondaryButtonText = 'Cancel',
  onPrimaryPress,
  onSecondaryPress,
  primaryButtonDisabled = false,
  primaryButtonLoading = false,
  hideButtons = false,
  width,
  maxWidth = 419,
  showCloseIcon = true,
  titleAlign = 'center',
  customStyle,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const screenWidth = Dimensions.get('window').width;
  const screenHeight = Dimensions.get('window').height;
  const isMobile = screenWidth < 768;

  const modalWidth = width || (isMobile ? screenWidth - 32 : Math.min(maxWidth, screenWidth - 32));

  const handleSecondaryPress = () => {
    if (onSecondaryPress) {
      onSecondaryPress();
    } else {
      onClose();
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      {/* Overlay */}
      <TouchableOpacity 
        style={styles.overlay} 
        onPress={onClose}
        activeOpacity={1}
      >
        {/* Modal Container */}
        <View style={styles.modalContainer}>
          <TouchableOpacity
            style={[
              styles.modalContent,
              { 
          width: modalWidth as number,
          maxHeight: screenHeight - 50,
              },
              customStyle
            ]}
            onPress={(e) => e.stopPropagation()}
            activeOpacity={1}
          >
            {/* Close Button */}
            {showCloseIcon && (
              <TouchableOpacity
          style={styles.closeButton}
          onPress={onClose}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
          <CloseIcon color={isDark ? '#FFFFFF' : '#3A3A3A'} />
              </TouchableOpacity>
            )}

            {/* Modal Body */}
            <View style={styles.modalBody}>
              {/* Title */}
              <View style={[styles.titleContainer, titleAlign === 'left' && styles.titleContainerLeft]}>
          <Text style={[styles.title, { color: '#000000', fontSize: 18 }]} numberOfLines={2}>
            {title}
          </Text>
              </View>

              {/* Content with Scroll */}
              <View style={{ flex: 1, maxHeight: screenHeight - 200, paddingRight: 8 }}>
          <ScrollView
            contentContainerStyle={styles.contentContainer}
            showsVerticalScrollIndicator={true}
            bounces={false}
            style={{ paddingRight: 12 }}
          >
            {children}
          </ScrollView>
              </View>
            </View>

            {/* Footer with Buttons */}
            {!hideButtons && (
              <View style={[
          styles.buttonContainer,
          !secondaryButtonText && { justifyContent: 'center' }
              ]}>
          {secondaryButtonText && (
            <DLOButton
              text={secondaryButtonText}
              variant="tertiary"
              size="medium"
              onPress={handleSecondaryPress}
            />
          )}
          <DLOButton
            text={primaryButtonText}
            variant="primary"
            size="medium"
            onPress={onPrimaryPress}
            disabled={primaryButtonDisabled}
            isLoading={primaryButtonLoading}
          />
              </View>
            )}
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    </Modal>
  );
};

// Specialized Location Selection Modal
export interface DLOLocationModalProps {
  visible: boolean;
  onClose: () => void;
  selectedLocation?: DLODropdownOption | null;
  locationOptions: DLODropdownOption[];
  onLocationSelect: (location: DLODropdownOption | null) => void;
  onSave?: () => void;
  isLoading?: boolean;
}

export const DLOLocationModal: React.FC<DLOLocationModalProps> = ({
  visible,
  onClose,
  selectedLocation,
  locationOptions,
  onLocationSelect,
  onSave,
  isLoading = false,
}) => {
  const handleLocationSelect = (option: DLODropdownOption | DLODropdownOption[] | null) => {
    // Handle the case where option could be an array or single option
    if (Array.isArray(option)) {
      // If it's an array, take the first item or null if empty
      onLocationSelect(option.length > 0 ? option[0] : null);
    } else {
      // If it's a single option or null, pass it directly
      onLocationSelect(option);
    }
  };

  return (
    <DLOModal
      visible={visible}
      onClose={onClose}
      title="Select Location"
      primaryButtonText="Save"
      secondaryButtonText="Cancel"
      onPrimaryPress={onSave}
      primaryButtonLoading={isLoading}
      primaryButtonDisabled={!selectedLocation}
    >
      <DLODropdown
        label="Location"
        value={selectedLocation}
        options={locationOptions}
        onSelect={handleLocationSelect}
        placeholder="Select a location"
      />
    </DLOModal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  modalContent: {
    backgroundColor: '#FBFCFE',
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#D7E5F8',
    shadowColor: 'rgba(0, 92, 188, 0.08)',
    shadowOffset: {
      width: 0,
      height: 0,
    },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 8,
    minWidth: 330,
    position: 'relative',
    ...Platform.select({
      web: {
        boxShadow: '0 0 24px 0 rgba(0, 92, 188, 0.08)',
      },
    }),
    padding: '2%',
  },
  closeButton: {
    position: 'absolute',
    // top: "14%",
    right: "6%",
    zIndex: 1000,
    padding: 4,
  },
  modalBody: {
    paddingBottom: 8,
    gap: 16,
  },
  titleContainer: {
    paddingRight: 24,
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
  },
  titleContainerLeft: {
    alignItems: 'flex-start',
  },
  title: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 24,
    fontWeight: '700',
    lineHeight: 32,
    flex: 1,
  },
  contentContainer: {
    gap: 16,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 16,
    marginTop: 16,
  },
});

export default DLOModal;
